"use client";

import { useState } from "react";
import Image from "next/image";
import { motion, useReducedMotion } from "framer-motion";
import { Clock, Presentation } from "lucide-react";
import {
  synchronizedTimeline,
  type ScheduleSession,
  type SessionType,
  type SynchronizedRow,
} from "@/lib/agenda-data";
import { speakers as dbSpeakers } from "@/lib/content";

const EASE = [0.22, 1, 0.36, 1] as const;

type FilterCategory = "todos" | "keynote" | "panel" | "charla" | "workshop";
type MobileDayTab = "dia-1" | "dia-2" | "ambos";

const filterOptions: { id: FilterCategory; label: string; shortLabel: string }[] =
  [
    { id: "todos", label: "Todas las sesiones", shortLabel: "Todas" },
    { id: "keynote", label: "Keynotes", shortLabel: "Keynotes" },
    { id: "panel", label: "Paneles", shortLabel: "Paneles" },
    { id: "charla", label: "Charlas & Casos", shortLabel: "Charlas" },
    { id: "workshop", label: "Talleres & Dinámicas", shortLabel: "Talleres" },
  ];

function sessionMatchesFilter(
  session: ScheduleSession,
  filter: FilterCategory
): boolean {
  if (filter === "todos") return true;
  if (filter === "keynote") return session.type === "keynote";
  if (filter === "panel") return session.type === "panel";
  if (filter === "charla")
    return (
      session.type === "charla" ||
      session.type === "entrevista" ||
      session.type === "bienvenida" ||
      session.type === "cierre"
    );
  if (filter === "workshop")
    return session.type === "workshop" || session.type === "speed-dating";
  return true;
}

/**
 * Category colors:
 * - Keynote: #1DD2B3 (Mint Green Oficial)
 * - Panel: #6666FF (Indigo / Azul Eléctrico)
 * - Charla / Bienvenida / Cierre / Entrevista: #ED1E79 (Magenta / Rosa)
 * - Talleres / Workshops / Speed Dating: #9FE870 (Verde Lima)
 * - Breaks: #555558 (Neutro)
 */
function getBadgeStyle(type: SessionType): {
  bg: string;
  text: string;
  bar: string;
  titleColor: string;
} {
  switch (type) {
    case "keynote":
      return {
        bg: "bg-[#1DD2B3]/20",
        text: "text-[#2EEAD0]",
        bar: "#1DD2B3",
        titleColor: "text-white",
      };
    case "panel":
      return {
        bg: "bg-[#6666FF]/20",
        text: "text-[#8888FF]",
        bar: "#6666FF",
        titleColor: "text-white",
      };
    case "charla":
    case "bienvenida":
    case "cierre":
    case "entrevista":
      return {
        bg: "bg-[#ED1E79]/20",
        text: "text-[#FF4D94]",
        bar: "#ED1E79",
        titleColor: "text-white",
      };
    case "workshop":
    case "speed-dating":
      return {
        bg: "bg-[#9FE870]/20",
        text: "text-[#B8F090]",
        bar: "#9FE870",
        titleColor: "text-white",
      };
    case "break":
    default:
      return {
        bg: "bg-white/10",
        text: "text-white/60",
        bar: "#555558",
        titleColor: "text-white/60",
      };
  }
}

function LinkedInIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      width={15}
      height={15}
      fill="currentColor"
      aria-hidden
      focusable="false"
      className="shrink-0"
    >
      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.225 0z" />
    </svg>
  );
}

/**
 * Normalizes time strings to clean 2-digit zero-padded tabular format:
 * e.g. "8:30 A.M." -> "08:30 AM", "14:25 P.M." -> "14:25 PM"
 */
function formatDisplayTime(timeStr: string): string {
  const cleaned = timeStr.trim();
  const match = cleaned.match(/^(\d{1,2}):(\d{2})\s*([AP]\.?M\.?)?$/i);
  if (match) {
    const hours = match[1].padStart(2, "0");
    const minutes = match[2];
    const period = match[3]
      ? match[3].toUpperCase().includes("P")
        ? "PM"
        : "AM"
      : "";
    return period ? `${hours}:${minutes} ${period}` : `${hours}:${minutes}`;
  }
  return cleaned;
}

function GraphicPosterCard({ session }: { session: ScheduleSession }) {
  const badge = getBadgeStyle(session.type);
  const [isHovered, setIsHovered] = useState(false);
  const displayTime = formatDisplayTime(session.time);

  if (session.isBreak) {
    return (
      <div className="relative flex items-center justify-between gap-2 h-[42px] min-h-[42px] max-h-[42px] shrink-0 self-start w-full rounded-lg bg-[#111113] border border-white/[0.07] px-3.5 overflow-hidden">
        {/* Left vertical accent bar */}
        <div
          className="absolute left-0 top-0 bottom-0 w-[4px]"
          style={{ backgroundColor: badge.bar }}
        />

        <div className="flex items-center gap-2 pl-0.5 min-w-0">
          <span
            className={`px-2 py-0.5 rounded-full text-[9px] font-bold tracking-[0.12em] uppercase font-[family-name:var(--font-display-rounded)] shrink-0 ${badge.bg} ${badge.text}`}
          >
            {session.typeLabel}
          </span>
          {session.description && (
            <span className="hidden sm:inline text-white/45 text-[11.5px] font-[family-name:var(--font-body)] truncate">
              {session.description}
            </span>
          )}
        </div>

        {/* Time Pill on Right: EXACT SAME SIZE (w-[104px] h-[26px]) */}
        <span className="w-auto min-w-[104px] h-[26px] px-2.5 flex items-center justify-center gap-1.5 font-mono text-[12px] font-bold tabular-nums text-white bg-white/[0.08] rounded-md border border-white/14 shrink-0 shadow-xs">
          <Clock className="w-3.5 h-3.5 text-white/80" />
          {displayTime}
        </span>
      </div>
    );
  }

  return (
    <div
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      className={`group relative flex flex-col justify-between h-full w-full rounded-xl bg-[#151517] border px-3.5 py-2.5 sm:px-4 sm:py-3 transition-all duration-200 text-left ${
        isHovered
          ? "border-white/35 bg-[#18181C] shadow-[0_12px_32px_rgba(0,0,0,0.85)]"
          : "border-white/[0.08] hover:border-white/20"
      }`}
    >
      {/* Left thick vertical accent bar (poster style) */}
      <div
        className={`absolute left-0 top-0 bottom-0 transition-all duration-200 rounded-l-xl ${
          isHovered ? "w-[6px]" : "w-[4px]"
        }`}
        style={{ backgroundColor: badge.bar }}
      />

      <div className="pl-1 space-y-1">
        {/* Top row: auditorium marker on left + time pill on right */}
        <div className="flex items-center justify-between gap-2 pb-0.5">
          {session.auditorium ? (
            <span className="flex min-w-0 items-center gap-1.5 text-[11px] sm:text-[12px] text-white/70 font-[family-name:var(--font-body)]">
              <Presentation
                className="h-3.5 w-3.5 shrink-0 text-white/55"
                strokeWidth={1.5}
                aria-hidden="true"
              />
              <span className="truncate">
                Auditorio{" "}
                <strong className="font-semibold text-white">
                  {session.auditorium}
                </strong>
              </span>
            </span>
          ) : (
            <span
              className={`px-2.5 py-0.5 rounded-full text-[9.5px] sm:text-[10px] font-bold tracking-[0.14em] uppercase font-[family-name:var(--font-display-rounded)] ${badge.bg} ${badge.text}`}
            >
              {session.typeLabel}
            </span>
          )}

          <span className="w-auto min-w-[104px] h-[26px] px-2.5 flex items-center justify-center gap-1.5 font-mono text-[12px] font-bold tabular-nums text-white bg-white/[0.08] rounded-md border border-white/14 shrink-0 shadow-xs">
            <Clock className="w-3.5 h-3.5 text-white/80" />
            {displayTime}
          </span>
        </div>

        {/* Title */}
        <h4
          className={`font-[family-name:var(--font-display)] font-bold text-[14px] sm:text-[15px] leading-[1.22] ${badge.titleColor} transition-colors`}
        >
          {session.title}
        </h4>

        {/* Multi-subsessions specifically for Sponsors / Partners (Compact horizontal inline flow) */}
        {session.speakers &&
          session.speakers.some((s) => s.time) && (
            <div className="mt-1.5 pt-1.5 border-t border-white/[0.06] flex flex-wrap items-center gap-x-3 gap-y-1.5">
              {session.speakers.map((sp, idx) => {
                const isPartnerLogo = Boolean(
                  sp.photo && sp.photo.includes("/partners/")
                );
                const showInst =
                  sp.institution &&
                  sp.institution.toLowerCase().replace(/[^a-z0-9]/g, "") !==
                    sp.name.toLowerCase().replace(/[^a-z0-9]/g, "");

                return (
                  <div
                    key={idx}
                    className="flex items-center gap-1.5 text-[11px] sm:text-xs text-white/80 font-[family-name:var(--font-body)]"
                  >
                    {isPartnerLogo && (
                      <div className="relative h-5 w-5 shrink-0 overflow-hidden rounded-full bg-white border border-white shadow-xs p-0.5">
                        <Image
                          src={sp.photo!}
                          alt={sp.name}
                          fill
                          className="object-contain"
                          sizes="20px"
                        />
                      </div>
                    )}
                    <span className="text-[11.5px] text-white/85">
                      <strong className="font-semibold text-white">
                        {sp.name}
                      </strong>
                      {showInst && (
                        <span className="text-white/45">
                          , {sp.institution}
                        </span>
                      )}
                      .
                    </span>
                    {sp.time && (
                      <span className="font-mono text-[10.5px] text-white/50 font-semibold shrink-0">
                        ({sp.time})
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          )}

        {/* Normal speakers list in sentence format (Visible directly on card) */}
        {session.speakers &&
          !session.speakers.some((s) => s.time) &&
          session.speakers.length > 0 && (
            <p className="text-[11.5px] sm:text-[12.5px] leading-[1.4] text-white/65 font-[family-name:var(--font-body)]">
              {session.type === "workshop" && session.speakers.length === 1 ? (
                <span>
                  Dictado por{" "}
                  <strong className="font-semibold text-white/95">
                    {session.speakers[0].name}
                  </strong>
                  .
                </span>
              ) : (
                session.speakers.map((s, i) => {
                  const isUnannounced =
                    s.name.toLowerCase().includes("panelistas") ||
                    s.name.toLowerCase().includes("pendiente") ||
                    s.name.toLowerCase().includes("por anunciar") ||
                    (s.institution &&
                      (s.institution.toLowerCase().includes("pendiente") ||
                        s.institution.toLowerCase().includes("por anunciar")));

                  if (isUnannounced) {
                    return (
                      <span key={i}>
                        <strong className="font-semibold text-white/95">
                          {s.name}
                        </strong>{" "}
                        <span className="italic text-white/40">por anunciar.</span>
                      </span>
                    );
                  }

                  const showInst =
                    s.institution &&
                    s.institution.toLowerCase().replace(/[^a-z0-9]/g, "") !==
                      s.name.toLowerCase().replace(/[^a-z0-9]/g, "");

                  return (
                    <span key={i}>
                      <strong className="font-semibold text-white/95">
                        {s.name}
                      </strong>
                      {showInst && (
                        <span className="text-white/50">, {s.institution}</span>
                      )}
                      {i < session.speakers!.length - 1 &&
                        (s.breakAfter ? (
                          <br className="hidden sm:inline" />
                        ) : (
                          " · "
                        ))}
                    </span>
                  );
                })
              )}
            </p>
          )}

        {/* Moderator */}
        {session.moderator && (
          <p className="text-[11px] text-white/55 font-[family-name:var(--font-body)]">
            {session.moderator.toLowerCase().includes("pendiente") ||
            session.moderator.toLowerCase().includes("por anunciar") ? (
              <>
                <strong className="font-semibold text-white/85">
                  Moderador
                </strong>{" "}
                <span className="italic text-white/40">por anunciar.</span>
              </>
            ) : (
              <>
                <strong className="font-semibold text-white/85">
                  {session.moderator.startsWith("Modera ")
                    ? "Modera: "
                    : session.moderator.startsWith("Moderadora")
                    ? "Moderadora: "
                    : "Moderador: "}
                </strong>
                <span>
                  {session.moderator.replace(/^(Modera|Moderadora|Moderador)\s*:?\s*/i, "")}
                </span>
              </>
            )}
          </p>
        )}

        {/* In-Place Smooth Expansion on Hover: Reveals Description only (No duplication) */}
        {session.description && (
          <div
            className={`grid transition-all duration-300 ease-out overflow-hidden ${
              isHovered
                ? "grid-rows-[1fr] opacity-100 mt-2.5 pt-2 border-t border-white/12"
                : "grid-rows-[0fr] opacity-0 mt-0 pt-0 border-t-0"
            }`}
          >
            <div className="min-h-0">
              <p className="text-[12px] sm:text-[12.5px] leading-[1.48] text-white/75 font-[family-name:var(--font-body)]">
                {session.description}
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function SessionCellGroup({
  sessions,
  activeFilter,
}: {
  sessions: ScheduleSession[];
  activeFilter: FilterCategory;
}) {
  const filtered = sessions.filter((s) => sessionMatchesFilter(s, activeFilter));

  if (filtered.length === 0) {
    return null;
  }

  const isDual = filtered.length > 1;
  const onlyBreaks = filtered.every((s) => s.isBreak);

  return (
    <div
      className={`grid gap-2 sm:gap-2.5 ${
        onlyBreaks
          ? "items-start content-start h-[42px]"
          : "items-stretch h-full"
      } ${isDual ? "grid-cols-1 sm:grid-cols-2" : "grid-cols-1"}`}
    >
      {filtered.map((s) => (
        <GraphicPosterCard key={s.id} session={s} />
      ))}
    </div>
  );
}

function BlockBar({ label, range }: { label: string; range?: string }) {
  return (
    <div
      className={`w-full flex items-center py-2 px-3.5 sm:px-4 rounded-xl bg-white text-ink shadow-[0_4px_14px_rgba(255,255,255,0.15)] ${
        range ? "justify-between" : ""
      }`}
    >
      <span className="font-[family-name:var(--font-display)] font-bold text-[13px] sm:text-[14px] tracking-[-0.01em]">
        {label}
      </span>
      {range ? (
        <span className="font-mono text-[11px] sm:text-[12px] font-bold text-ink/75 tabular-nums">
          {range}
        </span>
      ) : null}
    </div>
  );
}

function DayColumn({
  id,
  eyebrow,
  date,
  theme,
  amRange,
  amRows,
  pmLeadInRows,
  pmBlockRows,
  side,
  activeFilter,
  className,
}: {
  id: string;
  eyebrow: string;
  date: string;
  theme: string;
  amRange: string;
  amRows: SynchronizedRow[];
  pmLeadInRows: SynchronizedRow[];
  pmBlockRows: SynchronizedRow[];
  side: "day1" | "day2";
  activeFilter: FilterCategory;
  className?: string;
}) {
  const sessionsOf = (row: SynchronizedRow) =>
    side === "day1" ? row.day1Sessions : row.day2Sessions;

  return (
    <article
      id={id}
      className={`flex flex-col gap-2 sm:gap-2.5 rounded-[22px] sm:rounded-[26px] bg-[#0E0E10] border border-white/18 p-4 sm:p-5 lg:p-6 shadow-[0_20px_50px_rgba(0,0,0,0.7)] scroll-mt-40 lg:scroll-mt-28 ${className ?? ""}`}
    >
      <div className="pb-3 border-b border-white/12 flex items-baseline justify-between gap-4">
        <div>
          <div className="text-[10.5px] sm:text-[11px] font-bold uppercase tracking-[0.2em] font-[family-name:var(--font-body)] text-white/50">
            {eyebrow}
          </div>
          <h3 className="font-[family-name:var(--font-display)] font-bold text-[24px] sm:text-[34px] leading-[1] mt-0.5 text-white">
            {date}
          </h3>
        </div>
        <span className="text-[9.5px] sm:text-[10.5px] font-semibold tracking-wider text-white/40 uppercase bg-white/[0.04] px-2.5 py-0.5 rounded-full border border-white/10 shrink-0">
          {theme}
        </span>
      </div>

      <BlockBar label="Bloque Mañana (AM)" range={amRange} />
      {amRows.map((row) => (
        <SessionCellGroup
          key={`${id}-am-${row.id}`}
          sessions={sessionsOf(row)}
          activeFilter={activeFilter}
        />
      ))}
      {pmLeadInRows.map((row) => (
        <SessionCellGroup
          key={`${id}-lead-${row.id}`}
          sessions={sessionsOf(row)}
          activeFilter={activeFilter}
        />
      ))}
      <BlockBar label="Bloque Tarde (PM)" />
      {pmBlockRows.map((row) => (
        <SessionCellGroup
          key={`${id}-pm-${row.id}`}
          sessions={sessionsOf(row)}
          activeFilter={activeFilter}
        />
      ))}
    </article>
  );
}

export function Jornadas() {
  const reduced = useReducedMotion();
  const [activeFilter, setActiveFilter] = useState<FilterCategory>("todos");
  const [mobileDayTab, setMobileDayTab] = useState<MobileDayTab>("ambos");

  const visibleRows = synchronizedTimeline.filter((row) => {
    if (activeFilter === "todos") return true;
    if (row.isBreakRow) return false;
    const hasD1 = row.day1Sessions.some((s) =>
      sessionMatchesFilter(s, activeFilter)
    );
    const hasD2 = row.day2Sessions.some((s) =>
      sessionMatchesFilter(s, activeFilter)
    );
    return hasD1 || hasD2;
  });

  const amRows = visibleRows.filter((r) => r.block === "am");
  const pmRows = visibleRows.filter((r) => r.block === "pm");
  const isPmLeadIn = (id: string) =>
    id === "time-1235" || id === "time-1325-break";
  const pmLeadInRows = pmRows.filter((r) => isPmLeadIn(r.id));
  const pmBlockRows = pmRows.filter((r) => !isPmLeadIn(r.id));

  const scrollToDay = (dayId: string, tab: MobileDayTab) => {
    setMobileDayTab(tab);
    setTimeout(() => {
      const el = document.getElementById(dayId);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }, 50);
  };

  return (
    <section
      id="programa"
      className="bg-night text-white px-4 sm:px-6 lg:px-8 pt-14 sm:pt-16 pb-12 sm:pb-16 scroll-mt-24"
    >
      <div className="mx-auto max-w-[1440px]">
        {/* Section Header (Clean: Title only, no pretitle, no subtitle, no cupos) */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6 sm:mb-8">
          <motion.h2
            initial={reduced ? false : { opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.6, ease: EASE }}
            className="font-[family-name:var(--font-display)] font-bold leading-[0.96] tracking-[-0.035em] text-white"
            style={{ fontSize: "clamp(34px, 4.8vw, 64px)" }}
          >
            Programa del Evento.
          </motion.h2>

          {/* Venue Pill */}
          <div className="flex items-center gap-2 p-2.5 sm:p-3 rounded-2xl bg-white/[0.03] border border-white/10 w-fit shrink-0">
            <span className="flex h-2 w-2 rounded-full bg-[#1DD2B3] animate-pulse" />
            <span className="text-xs text-white/80 font-medium">
              Fundación Chile · Vitacura, Santiago
            </span>
          </div>
        </div>

        {/* Desktop filter chips */}
        <div className="hidden lg:flex items-center gap-2 overflow-x-auto pb-2 mb-6 scrollbar-none">
          {filterOptions.map((f) => {
            const active = activeFilter === f.id;
            return (
              <button
                key={f.id}
                type="button"
                onClick={() => setActiveFilter(f.id)}
                className={`shrink-0 px-3.5 py-1.5 rounded-full text-xs font-semibold tracking-wide transition-all duration-200 cursor-pointer ${
                  active
                    ? "bg-white text-ink shadow-[0_2px_12px_rgba(255,255,255,0.2)]"
                    : "bg-white/[0.04] text-white/70 border border-white/10 hover:border-white/30 hover:text-white"
                }`}
              >
                {f.label}
              </button>
            );
          })}
        </div>

        {/* Mobile: sticky day switcher + compact type chips */}
        <div className="lg:hidden sticky top-20 z-20 -mx-4 mb-5 bg-night/92 backdrop-blur-md border-b border-white/10 px-4 py-2.5">
          <div className="p-1 rounded-2xl bg-[#131316] border border-white/12 flex items-center gap-1">
            <button
              type="button"
              onClick={() => scrollToDay("dia-1", "dia-1")}
              className={`flex-1 py-2 px-2 rounded-xl text-xs font-bold transition-all text-center cursor-pointer ${
                mobileDayTab === "dia-1"
                  ? "bg-white text-ink shadow-sm"
                  : "text-white/75 hover:text-white hover:bg-white/5"
              }`}
            >
              Día 1 · 02 sep
            </button>
            <button
              type="button"
              onClick={() => scrollToDay("dia-2", "dia-2")}
              className={`flex-1 py-2 px-2 rounded-xl text-xs font-bold transition-all text-center cursor-pointer ${
                mobileDayTab === "dia-2"
                  ? "bg-white text-ink shadow-sm"
                  : "text-white/75 hover:text-white hover:bg-white/5"
              }`}
            >
              Día 2 · 03 sep
            </button>
            <button
              type="button"
              onClick={() => scrollToDay("programa", "ambos")}
              className={`flex-1 py-2 px-2 rounded-xl text-xs font-bold transition-all text-center cursor-pointer ${
                mobileDayTab === "ambos"
                  ? "bg-white text-ink shadow-sm"
                  : "text-white/75 hover:text-white hover:bg-white/5"
              }`}
            >
              Ambos
            </button>
          </div>
          <div className="flex items-center gap-1.5 overflow-x-auto pt-2 scrollbar-none">
            {filterOptions.map((f) => {
              const active = activeFilter === f.id;
              return (
                <button
                  key={f.id}
                  type="button"
                  onClick={() => setActiveFilter(f.id)}
                  className={`shrink-0 px-3 py-1 rounded-full text-[11px] font-semibold tracking-wide transition-all duration-200 cursor-pointer ${
                    active
                      ? "bg-white text-ink"
                      : "bg-white/[0.04] text-white/70 border border-white/10"
                  }`}
                >
                  {f.shortLabel}
                </button>
              );
            })}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 lg:gap-x-6">
          <DayColumn
            id="dia-1"
            eyebrow="Miércoles · Día 1"
            date="02 Septiembre"
            theme="Innovar · Adoptar"
            amRange="08:30 — 13:25"
            amRows={amRows}
            pmLeadInRows={pmLeadInRows}
            pmBlockRows={pmBlockRows}
            side="day1"
            activeFilter={activeFilter}
            className={mobileDayTab === "dia-2" ? "max-lg:hidden" : undefined}
          />
          <DayColumn
            id="dia-2"
            eyebrow="Jueves · Día 2"
            date="03 Septiembre"
            theme="Escalar · Operar"
            amRange="08:30 — 13:25"
            amRows={amRows}
            pmLeadInRows={pmLeadInRows}
            pmBlockRows={pmBlockRows}
            side="day2"
            activeFilter={activeFilter}
            className={mobileDayTab === "dia-1" ? "max-lg:hidden" : undefined}
          />
        </div>

        <div
          id="mapa"
          className="mt-5 lg:mt-6 w-full rounded-xl bg-white text-ink shadow-[0_4px_14px_rgba(255,255,255,0.15)] p-2 sm:p-3 scroll-mt-28"
        >
          <Image
            src="/img/mapa-evento.png"
            alt="Mapa del venue Fundación Chile: auditorios Transforma y Evoluciona, carpas, hall, ingreso y acreditación"
            width={2000}
            height={1441}
            className="w-full h-auto block"
            sizes="(max-width: 1440px) 100vw, 1440px"
          />
        </div>
      </div>
    </section>
  );
}
